// src/services/research/sqlTables.js
// Extrahiert die referenzierten Tabellennamen aus einem SQL-Query (heuristisch,
// clientseitig — bewusst KEIN vollwertiger SQL-Parser, siehe ADR 0029).
//
// Erkannt werden Tabellenreferenzen nach FROM / JOIN / INTO / UPDATE / USING /
// TABLE (CREATE/TRUNCATE/ALTER) inkl. Schema-Qualifizierung (schema.tabelle)
// und quotierter Bezeichner ("t", [t], `t`). Komma-Listen nach FROM werden
// mitgenommen. CTE-Namen (WITH x AS (…)) sind KEINE Datenquellen und werden
// herausgefiltert; ebenso Funktionsaufrufe nach FROM/JOIN (z.B. generate_series()).

// Ein (ggf. quotierter) Bezeichner, optional mehrstufig schema-qualifiziert.
const IDENT = String.raw`(?:"[^"]+"|\[[^\]]+\]|\x60[^\x60]+\x60|[A-Za-z_][\w$]*)`;
const QUALIFIED = String.raw`${IDENT}(?:\s*\.\s*${IDENT})*`;

// Kommentare und String-Literale entfernen, damit deren Inhalt keine
// falschen Treffer produziert ('…' | -- … | /* … */).
const stripNoise = (sql) =>
  String(sql || "")
    .replace(/'(?:[^']|'')*'/g, "''")
    .replace(/--[^\n]*/g, " ")
    .replace(/\/\*[\s\S]*?\*\//g, " ");

// Quotes/Brackets je Segment entfernen, Segmente mit "." verbinden.
const normalizeIdent = (raw) =>
  raw
    .split(".")
    .map((seg) => seg.trim().replace(/^["`[]|["`\]]$/g, ""))
    .join(".");

// SQL-Schlüsselwörter, die die Heuristik fälschlich als Tabellenname treffen kann
// (z.B. "DELETE FROM" ohne Ziel, "JOIN LATERAL …", "UPDATE … SET").
const KEYWORD_BLOCKLIST = new Set([
  "select", "lateral", "unnest", "values", "set", "only", "on", "as", "table",
]);

export const extractSqlTables = (sql) => {
  const text = stripNoise(sql);
  if (!text.trim()) return [];

  // CTE-Namen einsammeln: WITH [RECURSIVE] name AS ( … ), name2 AS ( … )
  const cteNames = new Set();
  const cteRe = new RegExp(
    String.raw`\b(?:with(?:\s+recursive)?|\))\s*,?\s*(${IDENT})\s+as\s*\(|,\s*(${IDENT})\s+as\s*\(`,
    "gi"
  );
  let cteMatch;
  while ((cteMatch = cteRe.exec(text)) !== null) {
    const name = normalizeIdent(cteMatch[1] || cteMatch[2] || "");
    if (name) cteNames.add(name.toLowerCase());
  }

  // Tabellenreferenzen: Schlüsselwort + qualifizierter Bezeichner.
  // Gruppe 1 = Schlüsselwort, Gruppe 2 = Referenz.
  const refRe = new RegExp(
    String.raw`\b(from|join|into|update|using|table)\s+(${QUALIFIED})`,
    "gi"
  );

  // Erste Schreibweise gewinnt; Dedupe case-insensitiv.
  const bySpelling = new Map();
  const addRef = (raw, keyword, followedByParen) => {
    // Funktionsaufruf statt Tabelle (nur bei FROM/JOIN/USING relevant —
    // bei INSERT INTO t (spalten) ist die Klammer normal).
    if (followedByParen && ["from", "join", "using"].includes(keyword)) return;
    const name = normalizeIdent(raw);
    const key = name.toLowerCase();
    if (!name || KEYWORD_BLOCKLIST.has(key) || cteNames.has(key)) return;
    if (!bySpelling.has(key)) bySpelling.set(key, name);
  };

  let m;
  while ((m = refRe.exec(text)) !== null) {
    const keyword = m[1].toLowerCase();
    let tail = text.slice(m.index + m[0].length);
    addRef(m[2], keyword, /^\s*\(/.test(tail));

    // Komma-Liste nach FROM: "FROM a, b c, d" (Aliasse werden übersprungen).
    if (keyword === "from") {
      const listRe = new RegExp(String.raw`^\s*(?:${IDENT})?\s*,\s*(${QUALIFIED})`);
      let listMatch;
      while ((listMatch = listRe.exec(tail)) !== null) {
        const rest = tail.slice(listMatch[0].length);
        addRef(listMatch[1], "from", /^\s*\(/.test(rest));
        tail = rest;
      }
    }
  }

  return Array.from(bySpelling.values()).sort((a, b) =>
    a.localeCompare(b, "de", { sensitivity: "base" })
  );
};
